import useSettingsStore from '@store/settingsStore'
import { CLOUD_FRONT_URL } from 'config/constants'
import useBreakpoints from 'hooks/useBreakpoints'
import Image from 'next/image'
import CalendarIcon from 'public/icons/calendar-icon.svg'
import { Dispatch, SetStateAction } from 'react'
import DatePicker, {
  CalendarContainer,
  ReactDatePickerProps,
} from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FaChevronDown } from 'react-icons/fa'
import { cn } from 'utils/classnames'

interface DateRangePickerProps {
  startDate: Date | null
  endDate: Date | null
  setStartDate: Dispatch<SetStateAction<Date | null>>
  setEndDate: Dispatch<SetStateAction<Date | null>>
  portalId?: string
  customInputClassName?: string
  calendarIconClassName?: string
  monthsToShow?: number
  popperPlacement?: string
}

const DateRangePicker = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  customInputClassName,
  calendarIconClassName,
  monthsToShow,
  popperPlacement,
  ...rest
}: DateRangePickerProps & Partial<ReactDatePickerProps>) => {
  const handleDateChange = ([startDate, endDate]: [
    Date | null,
    Date | null,
  ]) => {
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const isPro = useSettingsStore((state) => state.isPro())

  return (
    <DatePicker
      {...rest}
      onChange={(e) => handleDateChange(e)}
      startDate={startDate}
      endDate={endDate}
      customInput={
        <div
          className={cn(
            'flex items-center justify-center gap-2 rounded border border-th-input-border bg-th-bkg-2 px-4 py-1.5 leading-none',
            customInputClassName,
          )}
        >
          <div className={calendarIconClassName}>
            <CalendarIcon />
          </div>
          <span className="text-sm font-medium leading-none text-th-fgd-1">
            {startDate?.getFullYear()}-
            {startDate !== null ? startDate.getMonth() + 1 : '-'}-
            {startDate?.getDate()}
          </span>
          <span className="text-sm font-medium leading-none text-th-fgd-3">
            to
          </span>
          <span className="text-sm font-medium leading-none text-th-fgd-1">
            {endDate?.getFullYear()}-
            {endDate !== null ? endDate.getMonth() + 1 : '-'}-
            {endDate?.getDate()}
          </span>
          <span className="cursor-pointer">
            <FaChevronDown fontSize={isPro ? 10 : 14} color="#565656" />
          </span>
        </div>
      }
      selectsRange={true}
      monthsShown={monthsToShow || 2}
      maxDate={new Date()}
      calendarContainer={Container}
      popperPlacement={popperPlacement || 'top'}
    >
      <div className="glass-background absolute bottom-0 left-0 flex w-full justify-between px-6 py-[10px]">
        <button
          className="rounded border border-th-input-border bg-th-bkg-5 px-3 py-2 text-th-button"
          onClick={() => {
            if (!startDate && !endDate) {
              setStartDate(new Date())
              setEndDate(new Date())
              return
            }

            setEndDate(new Date())
          }}
        >
          Today
        </button>

        <div className="flex items-center gap-2">
          <div className="w-[90px] rounded border border-th-input-border bg-th-bkg-5 px-3 py-2 text-center text-th-fgd-1">
            {startDate
              ? startDate.getFullYear() +
                '/' +
                (startDate.getMonth() + 1) +
                '/' +
                startDate.getDate()
              : '-'}
          </div>
          <div className="h-[1px] w-2 bg-th-fgd-3" />
          <div className="w-[90px] rounded border border-th-input-border bg-th-bkg-5 px-3 py-2 text-center text-th-fgd-1">
            {endDate
              ? endDate.getFullYear() +
                '/' +
                (endDate.getMonth() + 1) +
                '/' +
                endDate.getDate()
              : '-'}
          </div>
        </div>
      </div>
    </DatePicker>
  )
}
const Container = ({
  children,
  className,
}: {
  children: React.ReactNode
  className: string
}) => {
  const { below } = useBreakpoints()
  return (
    <div className={cn('relative', below.lg && 'left-16')}>
      <div className="glassmorphism-bg" />
      <div className="glassmorphism" />
      <Image
        alt=""
        src={CLOUD_FRONT_URL + '/images/bg-noise.png'}
        layout="fill"
        objectFit="cover"
      />
      <div className="absolute left-1/2 top-[55px] hidden h-[180px] w-[1px] bg-th-bkg-3 lg:block" />
      <CalendarContainer className={className}>
        <div>{children}</div>
      </CalendarContainer>
    </div>
  )
}

export default DateRangePicker
